import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BOOKING } from "@/constants/testIds";
import { Video, Users, CheckCircle2 } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackageCards = ({ tiers, selected, onSelect }) => (
  <div
    role="radiogroup"
    aria-label="Choose your package"
    className="grid gap-3 sm:grid-cols-3"
  >
    {tiers.map((t, i) => {
      const isSelected = selected === t.title;
      const isPopular = !!t.highlight;
      return (
        <button
          key={t.id || i}
          type="button"
          role="radio"
          aria-checked={isSelected}
          data-testid={`booking-package-card-${t.id || i}`}
          onClick={() => onSelect(t.title)}
          className={`relative text-left p-5 border-2 transition-all bg-white
            ${isSelected
              ? "border-[#CBA358] shadow-[4px_4px_0px_#0A2342]"
              : "border-[#E5DFD3] hover:border-[#0A2342]"}
            ${isPopular ? "sm:scale-[1.03] sm:z-10 bg-[#FDFBF7]" : ""}`}
        >
          {isPopular && (
            <div className="absolute -top-3 left-4 bg-[#0A2342] text-[#CBA358] px-2.5 py-1 text-[9px] uppercase tracking-[0.22em] font-bold whitespace-nowrap">
              Most Popular
            </div>
          )}

          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display text-lg sm:text-xl text-[#0A2342] leading-tight">
                {t.title}
              </div>
              <div className="mt-2 font-display text-2xl sm:text-3xl font-bold text-[#0A2342] leading-none">
                {t.price_text}
              </div>
            </div>
            <div
              className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all mt-1
                ${isSelected ? "border-[#CBA358] bg-[#CBA358]" : "border-[#5C636A]/40 bg-white"}`}
            >
              {isSelected && <div className="w-2 h-2 rounded-full bg-[#0A2342]" />}
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

export const Booking = () => {
  const { content } = useSiteContent();
  const packages = content.pricing_tiers || [];
  const defaultPackage =
    (packages.find((p) => p.highlight) || packages[0])?.title || "";

  const [form, setForm] = useState({
    name: "", email: "", phone: "", grade: "",
    session_type: "zoom", package: defaultPackage, message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [slots, setSlots] = useState({ remaining: 5, limit: 5, claimed: 0 });

  useEffect(() => {
    axios.get(`${API}/free-slots`).then((res) => setSlots(res.data)).catch(() => {});
  }, [content.free_slot_limit]);

  // Update default package once tiers load from API
  useEffect(() => {
    if (!form.package && defaultPackage) {
      setForm((f) => ({ ...f, package: defaultPackage }));
    }
  }, [defaultPackage, form.package]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email, and phone.");
      return;
    }
    if (!form.package) {
      toast.error("Please choose a package.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, form);
      setSuccess(res.data);
      toast.success(
        res.data.is_free_slot
          ? "You got a FREE counselling slot! We'll be in touch within 24h."
          : "Booking received. We'll reach out within 24h."
      );
      const updated = await axios.get(`${API}/free-slots`);
      setSlots(updated.data);
      setForm({
        name: "", email: "", phone: "", grade: "",
        session_type: "zoom", package: defaultPackage, message: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="py-24 lg:py-32 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#CBA358] font-semibold">
            {content.booking_eyebrow}
          </div>
          <h2 className="font-display text-4xl lg:text-5xl mt-4 text-[#0A2342] leading-[1.05] text-balance">
            {content.booking_headline}
          </h2>

          <div className="mt-8 p-6 bg-[#CBA358] text-[#0A2342]">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold">
              {content.booking_offer_label}
            </div>
            <div className="mt-2 font-display text-3xl font-bold leading-tight">
              {content.booking_offer_title}
            </div>
            <div
              data-testid={BOOKING.spotsRemaining}
              className="mt-3 inline-flex items-baseline gap-2 text-sm font-semibold"
            >
              <span className="text-3xl font-display">{slots.remaining}</span>
              <span className="opacity-70">/ {slots.limit} free slots left</span>
            </div>
          </div>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-4 p-5 border border-[#E5DFD3] bg-white">
              <Video className="w-6 h-6 text-[#CBA358] shrink-0 mt-1" />
              <div>
                <div className="font-display text-lg text-[#0A2342]">{content.booking_zoom_title}</div>
                <p className="text-sm text-[#5C636A]">{content.booking_zoom_desc}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 border border-[#E5DFD3] bg-white">
              <Users className="w-6 h-6 text-[#CBA358] shrink-0 mt-1" />
              <div>
                <div className="font-display text-lg text-[#0A2342]">{content.booking_inperson_title}</div>
                <p className="text-sm text-[#5C636A]">{content.booking_inperson_desc}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {success ? (
            <div
              data-testid={BOOKING.successAlert}
              className="border border-[#0A2342] bg-white p-10 anim-fade-up"
            >
              <CheckCircle2 className="w-10 h-10 text-[#CBA358]" />
              <h3 className="mt-5 font-display text-3xl text-[#0A2342]">
                You&apos;re in, {success.name.split(" ")[0]}.
              </h3>
              <p className="mt-3 text-[#5C636A] leading-relaxed max-w-md">
                {success.is_free_slot ? (
                  <>You&apos;ve claimed one of our <strong>free launch slots</strong> for the <strong>{success.package}</strong>. {content.founder_name?.split(" ")[0] || "Harshita"} will personally reach out to you on <strong>{success.phone}</strong> within 24 hours.</>
                ) : (
                  <>Thank you for booking the <strong>{success.package}</strong>. {content.founder_name?.split(" ")[0] || "Harshita"} will reach out to you on <strong>{success.phone}</strong> within 24 hours.</>
                )}
              </p>
              <button
                onClick={() => setSuccess(null)}
                className="mt-7 text-sm uppercase tracking-[0.22em] text-[#0A2342] underline underline-offset-4 hover:text-[#CBA358]"
              >
                Book another session →
              </button>
            </div>
          ) : (
            <form
              data-testid={BOOKING.form}
              onSubmit={submit}
              className="bg-white border border-[#E5DFD3] p-8 lg:p-10 space-y-6"
            >
              {packages.length > 0 && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <Label className="text-[10px] uppercase tracking-[0.22em] text-[#0A2342] font-bold">
                      Choose Your Package *
                    </Label>
                    {form.package && (
                      <span className="text-[11px] uppercase tracking-[0.18em] text-[#CBA358] font-semibold">
                        Selected: {form.package}
                      </span>
                    )}
                  </div>
                  <PackageCards
                    tiers={packages}
                    selected={form.package}
                    onSelect={(t) => setForm((f) => ({ ...f, package: t }))}
                  />
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <FormField label="Your Name *">
                  <Input data-testid={BOOKING.name} value={form.name} onChange={update("name")} placeholder="Harshita Sharma" required className={fieldCls} />
                </FormField>
                <FormField label="Phone *">
                  <Input data-testid={BOOKING.phone} value={form.phone} onChange={update("phone")} placeholder="+91 9876543210" required className={fieldCls} />
                </FormField>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <FormField label="Email *">
                  <Input data-testid={BOOKING.email} type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" required className={fieldCls} />
                </FormField>
                <FormField label="Class / Year">
                  <Input data-testid={BOOKING.grade} value={form.grade} onChange={update("grade")} placeholder="Class 11, UG-2, etc." className={fieldCls} />
                </FormField>
              </div>

              <FormField label="Session Type">
                <Select
                  value={form.session_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, session_type: v }))}
                >
                  <SelectTrigger data-testid={BOOKING.sessionType} className="rounded-none border-x-0 border-t-0 border-b-2 border-[#E5DFD3] focus:ring-0 px-0 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="zoom">{content.booking_zoom_title}</SelectItem>
                    <SelectItem value="in_person">{content.booking_inperson_title}</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Anything we should know? (optional)">
                <Textarea
                  data-testid={BOOKING.message}
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Tell us a bit about the student, current stream, or what's confusing you most..."
                  className="rounded-none border-2 border-[#E5DFD3] focus-visible:ring-0 focus-visible:border-[#0A2342]"
                />
              </FormField>

              <button
                type="submit"
                data-testid={BOOKING.submit}
                disabled={loading}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0A2342] text-white px-8 py-4 text-sm uppercase tracking-wider font-medium hover:bg-[#173B63] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Sending..." : "Reserve My Session →"}
              </button>
              <p className="text-xs text-[#5C636A]">
                By submitting you agree to be contacted by {content.logo_text_primary}{content.logo_text_accent} on the details above.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const fieldCls =
  "rounded-none border-x-0 border-t-0 border-b-2 border-[#E5DFD3] focus-visible:ring-0 focus-visible:border-[#0A2342] px-0";

const FormField = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-[10px] uppercase tracking-[0.22em] text-[#5C636A] font-semibold">{label}</Label>
    {children}
  </div>
);

export default Booking;
